<template>
  <div class="modal-mask" @click.self="close">
    <div class="modal">
      <h4>결제내역 수동 저장 목록 엑셀 업로드</h4>
      <!-- 스펙201: 업로드 버튼 위쪽에 엑셀에 어떤 컬럼이 들어있는지만 표시 -->
      <p class="guide">
        엑셀 컬럼(첫 행): <b>이름</b>, <b>로그인id</b>, <b>주문일자</b>, <b>결제일자</b>, <b>주문번호</b>, <b>업무종류</b>, <b>결제액</b>, <b>반품액</b>, <b>반품철회액</b>, <b>전체취소액</b><br />
        업무종류는 <b>결제 / 반품 / 반품철회 / 전체취소</b> 중 하나로 입력하세요. 업로드하면 결제내역 수동 저장 목록에 추가됩니다.
      </p>

      <!-- 상단 버튼 : 양식 다운받기 / 업로드 양식 찾기 -->
      <div class="pick-btns">
        <button class="ghost" @click="downloadTemplate">양식 다운받기</button>
        <button class="ghost blue" @click="pickFile">업로드 양식 찾기</button>
        <span v-if="fileName" class="fname">{{ fileName }}</span>
        <input ref="file" type="file" accept=".xlsx,.xls,.csv" class="hidden" @change="onFile" />
      </div>
      <!-- 스펙201: 파일 선택 후 미리 보기 항목은 표시하지 않는다(행 데이터 미표시). -->

      <div v-if="result" class="result">
        업로드 완료: {{ result.applied }}건<span v-if="result.skipped"> / 실패 {{ result.skipped }}건</span>
      </div>
      <ul v-if="result && result.errors && result.errors.length" class="errs">
        <li v-for="(er, i) in result.errors" :key="i">{{ er }}</li>
      </ul>
      <p v-if="error" class="error">{{ error }}</p>

      <!-- 하단 : 업로드 / 닫기 -->
      <div class="modal-btns">
        <button class="primary" :disabled="!rows.length || saving" @click="submit">
          {{ saving ? '업로드 중...' : '업로드' }}
        </button>
        <button class="ghost" @click="close">닫기</button>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import api from '../api';

// 엑셀 헤더(한/영 허용) → 값 추출
function pick(row, keys) {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') return String(row[k]).trim();
  }
  return '';
}
function num(row, keys) {
  const v = pick(row, keys);
  if (v === '') return 0;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export default {
  name: 'ManualPurchaseImportDialog',
  data() {
    return { rows: [], fileName: '', saving: false, error: '', result: null };
  },
  methods: {
    downloadTemplate() {
      // 스펙201: 빈 템플릿(헤더 1행만). 1 row 는 업로드 엑셀 컬럼과 동일.
      const ws = XLSX.utils.aoa_to_sheet(
        [['이름', '로그인id', '주문일자', '결제일자', '주문번호', '업무종류', '결제액', '반품액', '반품철회액', '전체취소액']]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '결제내역수동저장양식');
      XLSX.writeFile(wb, '결제내역수동저장_양식.xlsx');
    },
    pickFile() { this.$refs.file.click(); },
    onFile(e) {
      this.error = ''; this.result = null;
      const file = e.target.files[0];
      if (!file) return;
      this.fileName = file.name;
      const reader = new FileReader();
      reader.onload = evt => {
        try {
          const wb = XLSX.read(evt.target.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
          this.rows = json.map(r => ({
            name: pick(r, ['이름', 'name', 'Name']),
            loginId: pick(r, ['로그인id', '로그인 ID', '로그인ID', 'loginId', 'login_id']),
            buyDt: pick(r, ['주문일자', '주문 일자', 'buyDt', 'orderDate', 'date']),
            payDt: pick(r, ['결제일자', '결제 일자', 'payDt', 'payDate']),   // 29007: 결제일자 → pay_dt
            // 스펙217: 업로드 시 주문번호 앞에 'M-' 를 덧붙인다
            orderNo: (function (o) { return o ? ('M-' + o) : o; })(pick(r, ['주문번호', '주문 번호', 'orderNo', 'order_id', 'orderId'])),
            work: pick(r, ['업무종류', '업무', 'work']),
            amount: num(r, ['결제액', 'amount']),
            refundAmt: num(r, ['반품액', 'refundAmt']),
            refundCancelAmt: num(r, ['반품철회액', 'refundCancelAmt']),
            allRetAmt: num(r, ['전체취소액', 'allRetAmt'])
          })).filter(r => r.loginId);
          // 스펙201: 미리보기 미표시 — 행 데이터를 화면에 노출하지 않는다.
        } catch (err) {
          this.error = '엑셀 파싱 실패: ' + err.message;
        }
      };
      reader.readAsArrayBuffer(file);
    },
    async submit() {
      this.error = ''; this.result = null; this.saving = true;
      try {
        const res = await api.importManualPurchases(this.rows);
        this.result = res.data;
        this.$emit('imported');
        // 스펙201: 업로드 완료 후 팝업 닫기 (오류가 있어도 목록에 반영된 건은 반영됨)
        this.$emit('close');
      } catch (e) {
        this.error = e.message;
      } finally {
        this.saving = false;
      }
    },
    close() { this.$emit('close'); }
  }
};
</script>

<style scoped>
.modal-mask { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 60; }
.modal { background: #fff; padding: 22px; border-radius: 8px; width: 640px; max-width: 94vw; }
.modal h4 { margin: 0 0 10px; }
.guide { font-size: 12.5px; color: #555; background: #f6f8ff; padding: 10px; border-radius: 6px; line-height: 1.6; }
.pick-btns { display: flex; align-items: center; gap: 8px; margin: 14px 0 4px; flex-wrap: wrap; }
.pick-btns button { padding: 8px 14px; border-radius: 6px; cursor: pointer; border: 1px solid #ccc; background: #f5f5f5; }
.pick-btns .blue { background: #eef1f7; color: #2f6fed; border-color: #cfdcff; }
.pick-btns .fname { font-size: 12px; color: #2f6fed; }
.hidden { display: none; }
.result { margin-top: 12px; font-size: 13px; color: #1a7f37; }
.errs { margin: 6px 0 0; padding-left: 18px; max-height: 120px; overflow: auto; color: #d93636; font-size: 12px; }
.error { color: #d93636; font-size: 13px; margin-top: 10px; }
.modal-btns { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.modal-btns button { padding: 8px 16px; border-radius: 6px; cursor: pointer; border: 1px solid #ccc; }
.modal-btns .primary { background: #2f6fed; color: #fff; border-color: #2f6fed; }
.modal-btns .primary:disabled { background: #9db8ef; }
.modal-btns .ghost { background: #f5f5f5; }
</style>
